import { useEffect, useMemo, useRef } from 'react';
import * as d3 from 'd3';
import { RM_COLORS, rmTypeMix } from './mppHelpers';
import type { MPPRow } from './types';
import styles from './DailyDashboard.module.css';

const formatLbs = (n: number) => Math.round(n).toLocaleString();
const formatPct = (n: number) => `${(100 * n).toFixed(1)}%`;

export function RmDonut({
  rows,
  expanded = false,
  onExpand,
}: {
  rows: readonly MPPRow[];
  expanded?: boolean;
  onExpand?: () => void;
}) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const data = useMemo(() => rmTypeMix(rows), [rows]);
  const total = useMemo(() => data.reduce((a, d) => a + d.value, 0) || 1, [data]);
  const size = expanded ? 180 : 110;
  const innerR = expanded ? 55 : 33;
  const outerR = expanded ? 85 : 55;

  useEffect(() => {
    if (!svgRef.current) return;
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    const arc = d3
      .arc<d3.PieArcDatum<{ key: string; value: number }>>()
      .innerRadius(innerR)
      .outerRadius(outerR);
    const pie = d3
      .pie<{ key: string; value: number }>()
      .value((d) => d.value)
      .sort(null);
    svg
      .append('g')
      .attr('transform', `translate(${size / 2},${size / 2})`)
      .selectAll('path')
      .data(pie(data))
      .join('path')
      .attr('d', arc)
      .attr('fill', (d) => RM_COLORS[d.data.key] ?? '#94a3b8');
  }, [data, innerR, outerR, size]);

  return (
    <div className={styles.card}>
      <div className={styles.cardHd}>
        <div>
          <div className={styles.cardTitle}>Supply mix (rm type)</div>
          <div className={styles.cardSub}>Where the shipped lbs come from</div>
        </div>
        {onExpand && (
          <button
            type="button"
            className={styles.expandBtn}
            onClick={onExpand}
            title="Expand"
          >
            ⤢
          </button>
        )}
      </div>
      <div className={styles.donutBody}>
        <svg ref={svgRef} width={size} height={size} />
        <div className={styles.donutKey}>
          {data.map((d) => (
            <div key={d.key} className={styles.donutKeyRow}>
              <span
                className={styles.donutSwatch}
                style={{ background: RM_COLORS[d.key] ?? '#94a3b8' }}
              />
              <strong>{d.key}</strong>: {formatLbs(d.value)} lbs (
              {formatPct(d.value / total)})
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
